import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Pencil, Trash2, ChevronLeft, ChevronRight, Eye, Download, Users, UserCheck, DollarSign, ShoppingCart, UserX, Shield, Clock, RotateCcw, AlertTriangle, Plus, Upload } from 'lucide-react';
import DateFilter from '@/components/DateFilter';
import CreateUserDialog from '@/components/admin/CreateUserDialog';
import BulkImportDialog from '@/components/admin/BulkImportDialog';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('end_user'); // Default to end_user
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [activeView, setActiveView] = useState('all');

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15, role: 'end_user' };
      if (search) params.search = search;
      
      // Map activeView to backend query parameters
      if (activeView === 'active') {
        params.is_active = true;
      } else if (activeView === 'verified') {
        params.is_verified = true;
      } else if (activeView === 'unverified') {
        params.is_verified = false;
      } else if (activeView === 'suspended') {
        params.is_active = false;
      } else if (activeView === 'new') {
        params.created_within = 7;
      }
      
      if (dateFilter) {
        if (typeof dateFilter === 'object' && dateFilter.type === 'custom') {
          params.date_from = dateFilter.from;
          params.date_to = dateFilter.to;
        } else {
          params.date_filter = dateFilter;
        }
      }
      const res = await api.get('/admin/users', { params });
      setUsers(res.data.users || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFilter, activeView]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/users/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, []);

  useEffect(() => { 
    fetchUsers(); 
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/users/export', { 
        responseType: 'blob',
        params: { role: 'end_user' }
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customer data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setSearch('');
    setDateFilter(null);
    setActiveView('all');
    setPage(1);
    toast.success('Filters reset');
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email, role: user.role });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${editUser.id}`, editForm);
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      toast.success('User deleted');
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const toggleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleBulkAction = () => {
    if (!bulkAction) {
      toast.error('Please select an action');
      return;
    }
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setConfirmAction({
      type: bulkAction,
      count: selectedUsers.length,
      onConfirm: executeBulkAction
    });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        await Promise.all(selectedUsers.map(id => api.delete(`/admin/users/${id}`)));
        toast.success(`${selectedUsers.length} user(s) deleted`);
      } else if (bulkAction === 'export') {
        exportUsersToCSV(selectedUsers);
        toast.success(`${selectedUsers.length} user(s) exported`);
        setSelectedUsers([]);
        setBulkAction('');
        setShowConfirmDialog(false);
        return;
      }

      setSelectedUsers([]);
      setBulkAction('');
      setShowConfirmDialog(false);
      await fetchUsers();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  const exportUsersToCSV = (userIds) => {
    const selectedData = users.filter(u => userIds.includes(u.id));
    const headers = ['User ID', 'Name', 'Email', 'Role', 'Status', 'Total Orders', 'Total Spent', 'Created At'];
    const rows = selectedData.map(u => [
      u.user_number || 'N/A',
      `"${u.name?.replace(/"/g, '""')}"`,
      u.email,
      u.role,
      u.is_active ? 'Active' : 'Inactive',
      u.total_orders || 0,
      u.total_spent || 0,
      new Date(u.created_at).toLocaleString()
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `r2yn-users-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        if (users.length > 0) {
          if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
            toast.info('All users deselected');
          } else {
            setSelectedUsers(users.map(u => u.id));
            toast.info(`${users.length} users selected`);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [users, selectedUsers]);

  const roleBadge = (role) => {
    const map = { admin: 'bg-purple-50 text-purple-800', vendor: 'bg-amber-50 text-amber-800', delivery_worker: 'bg-blue-50 text-blue-800', end_user: 'bg-slate-50 text-slate-800' };
    return <Badge variant="outline" className={`text-xs ${map[role] || ''}`}>{role?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Customer Management</h1>
          <p className="text-muted-foreground mt-1">View, manage, and analyze all platform customers.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-button">
            <Plus className="h-4 w-4 mr-2" />
            Create User
          </Button>
          <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-button">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleExportAll} disabled={exporting} data-testid="export-all-button">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('all')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_customers || 0}</div>
              <p className="text-xs text-muted-foreground">All registered customers</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('active')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_customers || 0}</div>
              <p className="text-xs text-muted-foreground">Online in last 30 days</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('verified')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Verified</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.verified_customers || 0}</div>
              <p className="text-xs text-muted-foreground">Email verified</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('suspended')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suspended</CardTitle>
              <UserX className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.suspended_customers || 0}</div>
              <p className="text-xs text-muted-foreground">Account suspended</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('new')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New (7 Days)</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.new_customers || 0}</div>
              <p className="text-xs text-muted-foreground">Recent signups</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Filter Tabs */}
      <Tabs value={activeView} onValueChange={(value) => { setActiveView(value); setPage(1); }} className="w-full">
        <TabsList className="grid w-full grid-cols-6 h-auto p-1 bg-muted">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-all"
          >
            All Customers
          </TabsTrigger>
          <TabsTrigger 
            value="active"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-active"
          >
            Active
          </TabsTrigger>
          <TabsTrigger 
            value="verified"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-verified"
          >
            Verified
          </TabsTrigger>
          <TabsTrigger 
            value="unverified"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-unverified"
          >
            Unverified
          </TabsTrigger>
          <TabsTrigger 
            value="suspended"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-suspended"
          >
            Suspended
          </TabsTrigger>
          <TabsTrigger 
            value="new"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="tab-new"
          >
            New (7d)
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeView} className="mt-6">
          <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="users-search-input"
              />
            </div>
            <DateFilter 
              value={dateFilter}
              onChange={(value) => { setDateFilter(value); setPage(1); }}
              className="w-[200px]"
            />
          </div>

          {/* Bulk Actions Bar */}
          {selectedUsers.length > 0 && (
            <Card className="border-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="font-medium">{selectedUsers.length} user(s) selected</span>
                  <Select value={bulkAction} onValueChange={setBulkAction}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="export">Export to CSV</SelectItem>
                      <SelectItem value="delete">Delete Users</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleBulkAction} disabled={!bulkAction}>
                    {bulkAction === 'export' ? 'Export' : 'Apply'}
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedUsers([])}>
                    Clear Selection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-3 px-2 w-12">
                      <Checkbox 
                        checked={selectedUsers.length === users.length && users.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left py-3 px-2 font-medium">User ID</th>
                    <th className="text-left py-3 px-2 font-medium">Name</th>
                    <th className="text-left py-3 px-2 font-medium">Email</th>
                    <th className="text-left py-3 px-2 font-medium">Role</th>
                    <th className="text-left py-3 px-2 font-medium">Verified</th>
                    <th className="text-left py-3 px-2 font-medium">Status</th>
                    <th className="text-right py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr 
                      key={user.id} 
                      className="border-b last:border-0 hover:bg-[hsl(351,96%,31%,0.02)] hover:border-l-2 hover:border-l-[hsl(351,96%,31%,0.3)] transition-all duration-200"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                        />
                      </td>
                      <td className="py-3 px-2 font-mono text-xs cursor-pointer" onClick={() => navigate(`/admin/users/${user.id}`)}>
                        {user.user_number || 'N/A'}
                      </td>
                      <td className="py-3 px-2 font-medium cursor-pointer" onClick={() => navigate(`/admin/users/${user.id}`)}>{user.name}</td>
                      <td className="py-3 px-2 text-muted-foreground">{user.email}</td>
                      <td className="py-3 px-2">{roleBadge(user.role)}</td>
                      <td className="py-3 px-2">
                        <Badge variant="outline" className={user.is_verified ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}>
                          {user.is_verified ? 'Verified' : 'Unverified'}
                        </Badge>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center gap-1 text-xs ${user.is_online ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${user.is_online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          {user.is_online ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8" 
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            data-testid={`view-user-${user.id}`}
                            title="View Details"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8" 
                            onClick={() => handleEdit(user)} 
                            data-testid={`edit-user-${user.id}`}
                            title="Edit User"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive" 
                            onClick={() => setDeleteUser(user)} 
                            data-testid={`delete-user-${user.id}`}
                            title="Delete User"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="end_user">Customer</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="delivery_worker">Driver</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete {deleteUser?.name}? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmAction?.type === 'delete' && (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to <span className="font-bold text-rose-600">permanently delete {confirmAction.count} user(s)</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All user data, orders, and history will be permanently removed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => confirmAction?.onConfirm()} 
              className={confirmAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : ''}
            >
              {confirmAction?.type === 'delete' ? 'Delete Users' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <CreateUserDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchUsers}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityType="users"
        onSuccess={fetchUsers}
      />
    </div>
  );
}
